import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Award, TrendingUp, Users, Shield, CheckCircle2, XCircle, Clock, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SCORE_COLOR = (s) => s >= 80 ? 'text-emerald-400' : s >= 60 ? 'text-amber-400' : 'text-red-400';

export default function ComplianceScoreDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/api/hr-compliance/compliance-scores`, { withCredentials: true });
      setData(res);
    } catch { toast.error('Failed to load scores'); }
    setLoading(false);
  };

  if (loading) return <DashboardLayout><div className="text-center py-12"><p className="text-muted-foreground">Loading compliance scores...</p></div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="text-center py-12"><p className="text-muted-foreground">No data</p></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="compliance-score-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#00A6A0]" />Compliance Score Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Service location compliance scores for CQC PIR submissions</p>
        </div>

        {/* Org-wide score */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="glass-card border-[#00A6A0]/30 md:col-span-1" data-testid="org-score">
            <CardContent className="p-4 text-center">
              <p className={`text-4xl font-bold ${SCORE_COLOR(data.org_score)}`}>{data.org_score}%</p>
              <p className="text-xs text-muted-foreground">Overall Score</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{data.workforce.green}</p><p className="text-xs text-muted-foreground">GREEN</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{data.workforce.amber}</p><p className="text-xs text-muted-foreground">AMBER</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{data.workforce.red}</p><p className="text-xs text-muted-foreground">RED</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#00A6A0]">{data.total_staff}</p><p className="text-xs text-muted-foreground">Staff</p>
          </CardContent></Card>
        </div>

        {/* Category scores */}
        <Card className="glass-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Category Scores</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.category_scores.map(cat => (
                <div key={cat.category} className="flex items-center gap-3" data-testid={`cat-${cat.category}`}>
                  <span className="text-xs text-foreground w-28 flex-shrink-0">{cat.category}</span>
                  <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden relative">
                    <div className={`h-full rounded-full ${cat.score >= 80 ? 'bg-emerald-500/60' : cat.score >= 60 ? 'bg-amber-500/60' : 'bg-red-500/60'}`}
                      style={{ width: `${cat.score}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-foreground">{cat.score}%</span>
                  </div>
                  <div className="flex gap-1 text-[10px] flex-shrink-0 w-32 justify-end">
                    <span className="text-emerald-400">{cat.green}G</span>
                    <span className="text-amber-400">{cat.amber}A</span>
                    <span className="text-red-400">{cat.red}R</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Staff scores ranked */}
        <Card className="glass-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-base">Staff Compliance Ranking</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.staff_scores.map((s, idx) => (
                <div key={s.staff_id} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg" data-testid={`staff-score-${idx}`}>
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground flex-shrink-0">{idx + 1}</span>
                  <span className="text-xs text-foreground flex-1 min-w-0 truncate">{s.name}</span>
                  <div className="flex gap-1 text-[10px]">
                    <span className="text-emerald-400">{s.green}G</span>
                    <span className="text-amber-400">{s.amber}A</span>
                    <span className="text-red-400">{s.red}R</span>
                  </div>
                  <span className={`text-sm font-bold ${SCORE_COLOR(s.score)} w-12 text-right`}>{s.score}%</span>
                </div>
              ))}
              {data.staff_scores.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No staff scores</p>}
            </div>
          </CardContent>
        </Card>

        {/* Deployment readiness */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="glass-card border-emerald-500/20"><CardContent className="p-4 text-center">
            <p className="text-xl font-bold text-emerald-400">{data.deployment.authorised}</p><p className="text-xs text-muted-foreground">Authorised</p>
          </CardContent></Card>
          <Card className="glass-card border-red-500/20"><CardContent className="p-4 text-center">
            <p className="text-xl font-bold text-red-400">{data.deployment.blocked}</p><p className="text-xs text-muted-foreground">Blocked</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-xl font-bold text-[#00A6A0]">{data.deployment.total}</p><p className="text-xs text-muted-foreground">Total Checks</p>
          </CardContent></Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
